import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, Inject, Optional, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import { IInventionData, IInventionDataResponse } from 'src/app/core/models/results.model';
import { GeneralInnovationActivityResultsService } from 'src/app/features/results/services/general-innovation-activity-results.service';
import { NullableNumberPipe } from 'src/app/shared/pipes/nullable-number.pipe';
import { loadSidenavData } from 'src/app/state/action';
import { AppState, Tab } from 'src/app/state/app.state';
import { selectActiveTabDetails, selectSidenavDataByTabIdAndKey } from 'src/app/state/selectors';
import * as XLSX from 'xlsx';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-inventions-view',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, NullableNumberPipe, MatTooltipModule, ClipboardModule],
  templateUrl: './inventions-view.component.html',
  styleUrl: './inventions-view.component.css'
})
export class InventionsViewComponent implements OnInit, OnChanges {
  // Popup support inputs
  @Input() isPopup: boolean = false;
  @Input() popupCompanyName?: string;
  @Input() popupTheme?: string;

  // Theme-scoping inputs (popup uses these to AND-merge a cluster filter or pass a custom query).
  @Input() theme_search_type: 'cluster' | 'custom' | null = null;
  @Input() cluster_id: number | string | null = null;
  @Input() cluster_name: string | null = null;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: any,
    @Optional() private dialogRef?: MatDialogRef<InventionsViewComponent>
  ) {
    if (this.dialogData) {
      this.isPopup = this.dialogData.isPopup || false;
      this.popupCompanyName = this.dialogData.popupCompanyName;
      this.popupTheme = this.dialogData.popupTheme;
      this.tabId = this.dialogData.tabId;
      this.query = this.dialogData.query;
      this.entityids = this.dialogData.entityids;
      this.tab_name = this.dialogData.popupCompanyName;
      this.theme_search_type = this.dialogData.theme_search_type ?? null;
      this.cluster_id = this.dialogData.cluster_id ?? null;
      this.cluster_name = this.dialogData.cluster_name ?? null;
    }
  }

  @Input() tabId!: string;
  @Input() query: string | null = null;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  tabs$: Observable<Tab | undefined> = this.store.select(selectActiveTabDetails);

  searchType: string = '';
  showQueryBox: boolean = false;

  // Sequence guards: requests are not unsubscribed; only the latest response is rendered.
  private inventionsSeq = 0;
  private summarySeq = 0;

  ngOnInit(): void {
    // Data fetching is handled in ngOnChanges based on input combinations.
  }

  public initializePopupData(): void {
    if (this.isPopup) {
      this.searchType = 'company';
      this.getInventionsForPopup();
    }
  }

  pageSize = 10;
  currentPage = 0;

  get totalPages(): number {
    return Math.ceil(this.inventions_data.length / this.pageSize);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  get pagedInventions(): IInventionData[] {
    const start = this.currentPage * this.pageSize;
    return this.inventions_data.slice(start, start + this.pageSize);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isPopup'] || changes['popupCompanyName'] || changes['popupTheme'] ||
      changes['tabId'] || changes['query'] || changes['entityids'] ||
      changes['theme_search_type'] || changes['cluster_id'] || changes['cluster_name']
    ) {
      this.tabs$ = this.store.select(selectActiveTabDetails);
      this.inventions_data = [];
      this.currentPage = 0;
      this.showSpinner = true;
      this.inventions_data_loading_failed = false;
      this.showResults = false;
      this.noInventionsFound = false;
      this.summaryText = '';

      if (this.isPopup && this.entityids && this.tabId) {
        this.searchType = 'company';
        this.getInventionsForPopup();
      } else if (!this.isPopup) {
        this.store.select(selectActiveTabDetails).pipe(take(1)).subscribe(tab => {
          this.searchType = tab?.search_type ?? '';
        });
        this.getInventionsDataFromStore();
      }
    }
  }

  toggleQueryBox(): void {
    this.showQueryBox = !this.showQueryBox;
  }

  closePopup(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  getToday(): string {
    return new Date().toISOString().split('T')[0]
  }

  showResults: boolean = false;
  showSpinner: boolean = false;
  noInventionsFound: boolean = false;
  form = this.fb.group({
    date_start: [null],
    date_end: [null],
    quality_filter: ['all' as 'all' | 'high']
  });
  inventions_data: IInventionData[] = [];

  private themeParamsForRequest(): { theme_search_type?: string; cluster_id?: number | string | null; cluster_name?: string | null } | undefined {
    if (!this.theme_search_type) return undefined;
    return {
      theme_search_type: this.theme_search_type,
      cluster_id: this.cluster_id,
      cluster_name: this.cluster_name,
    };
  }

  getInventions(): Observable<IInventionDataResponse> {
    this.inventions_data = [];
    return this.apiService.getInventions(
      this.query,
      this.entityids,
      this.form.value.date_start ? new Date(this.form.value.date_start as string) : null,
      this.form.value.date_end ? new Date(this.form.value.date_end as string) : null,
      this.form.value.quality_filter,
      this.searchType,
      this.tabId,
      this.themeParamsForRequest(),
    );
  }

  summaryText: string = '';
  loadingSummary: boolean = false;
  generateSummary() {
    this.summaryText = "";
    this.loadingSummary = true;
    const seq = ++this.summarySeq;
    this.apiService.getAISummary(
      this.query,
      this.entityids,
      this.form.value.date_start ? new Date(this.form.value.date_start as string) : null,
      this.form.value.date_end ? new Date(this.form.value.date_end as string) : null,
      this.form.value.quality_filter,
      this.searchType,
      this.tabId,
      this.themeParamsForRequest(),
    ).subscribe({
      next: (res) => {
        if (seq !== this.summarySeq) return;
        this.summaryText = res.payload.summary;
        this.loadingSummary = false;
      },
      error: () => {
        if (seq !== this.summarySeq) return;
        alert("AI Summary Generation Failed!!");
        this.loadingSummary = false;
        this.summaryText = "";
      }
    });
  }

  onSubmit() {
    this.inventions_data = [];
    this.currentPage = 0;
    this.showSpinner = true;
    this.inventions_data_loading_failed = false;
    this.showResults = false;
    this.summaryText = '';

    this.apiService.invalidateInflight(this.apiService.inventionsKey({
      query: this.query,
      entityids: this.entityids,
      tabId: this.tabId,
      view: this.searchType,
      theme_search_type: this.theme_search_type,
      cluster_id: this.cluster_id,
      date_start: this.form.value.date_start ? new Date(this.form.value.date_start as string) : null,
      date_end: this.form.value.date_end ? new Date(this.form.value.date_end as string) : null,
      quality_filter: this.form.value.quality_filter,
    }));

    const seq = ++this.inventionsSeq;
    this.getInventions().subscribe({
      next: (res) => {
        if (seq !== this.inventionsSeq) return;
        if (res.payload.tabId != this.tabId) return;
        this.inventions_data = res.payload.results;
        if (this.inventions_data.length > 0) {
          this.showResults = true;
          this.inventions_data_loading_failed = false;
          this.showSpinner = false;
        } else {
          this.showSpinner = false;
          this.noInventionsFound = true;
        }
      },
      error: (err) => {
        if (seq !== this.inventionsSeq) return;
        console.error(err);
        this.showResults = false;
        this.inventions_data_loading_failed = true;
        this.showSpinner = false;
      }
    });
  }

  inventions_data_loading_failed: boolean = false;

  getInventionsDataFromStore() {
    this.showSpinner = true;
    this.summaryText = '';
    const seq = ++this.inventionsSeq;
    this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, 'inventions_data')).pipe(take(1)).subscribe(data => {
      if (seq !== this.inventionsSeq) return;
      if (data) {
        this.inventions_data = data;
        if (this.inventions_data.length == 0) {
          this.noInventionsFound = true;
        }
        this.inventions_data_loading_failed = false;
        this.showResults = true;
        this.showSpinner = false;
        return;
      }
      this.getInventions().subscribe({
        next: (res) => {
          if (seq !== this.inventionsSeq) return;
          if (res.payload.tabId != this.tabId) {
            this.store.dispatch(loadSidenavData({
              tabId: res.payload.tabId,
              sidenavKey: 'inventions_data',
              data: res.payload.results
            }));
            return;
          }
          this.inventions_data = res.payload.results;
          this.store.dispatch(loadSidenavData({
            tabId: this.tabId,
            sidenavKey: 'inventions_data',
            data: res.payload.results
          }));
          if (this.inventions_data.length > 0) {
            this.showResults = true;
            this.inventions_data_loading_failed = false;
            this.showSpinner = false;
          } else {
            this.showSpinner = false;
            this.noInventionsFound = true;
          }
          this.inventions_data_loading_failed = false;
        },
        error: (err) => {
          if (seq !== this.inventionsSeq) return;
          console.error(err);
          this.showSpinner = false;
          this.inventions_data_loading_failed = true;
        }
      });
    });
  }

  getInventionsForPopup() {
    this.currentPage = 0;
    this.showSpinner = true;
    this.summaryText = '';
    const seq = ++this.inventionsSeq;

    this.getInventions().subscribe({
      next: (res) => {
        if (seq !== this.inventionsSeq) return;
        this.inventions_data = res.payload.results;
        if (this.inventions_data.length > 0) {
          this.showResults = true;
          this.inventions_data_loading_failed = false;
          this.showSpinner = false;
          this.noInventionsFound = false;
          this.cdr.detectChanges();
        } else {
          this.noInventionsFound = true;
          this.showSpinner = false;
          this.cdr.detectChanges();
        }
      },
      error: () => {
        if (seq !== this.inventionsSeq) return;
        this.inventions_data_loading_failed = true;
        this.showSpinner = false;
        this.cdr.detectChanges();
      }
    });
  }

  downloadExcel(): void {
    const filteredList = this.inventions_data.map(item => ({
      'Family ID': item.family_id,
      'Invention Title': item.invention_title,
      'Date Of Publication': item.date_publication,
      'Entity IDs Filing': item.entityids_filing ? item.entityids_filing.join(', ') : '',
      'UCID': item.ucid,
      'Primary Cluster': item['cpc_cluster.label'],
      'Quality Score': Math.round(item.quality_score * 10) / 10,
      'Entity IDs': item.entityids ? item.entityids.join(', ') : '',
      'Applicants Filing': item.applicants_filing ? item.applicants_filing.join(', ') : '',
      'Applicants': item.applicants ? item.applicants.join(', ') : '',
      'Espacenet Link': item.link_espacenet,
    }));


    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(filteredList);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    ws['!cols'] = [
      { wch: 10 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
      { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }
    ];
    const sheetName = "Inventions_Data";
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const tabName = this.tab_name ? this.tab_name.substring(0, 30) : 'inventions';
    const fileName = this.tab_name ? `${tabName}_inventions.xlsx` : 'excel_file.xlsx';
    XLSX.writeFile(wb, fileName);
  }
}
